import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Smartphone, Bell, CreditCard, FileText, HardHat } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { APP_FEATURES } from "@/data/content";
import { Logo } from "@/components/Logo";
import { fetchAppSettings } from "@/lib/api";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PhoneMockup = () => (
    <div className="relative mx-auto w-[270px]" data-testid="app-phone-mockup">
        <div className="rounded-[36px] border border-white/15 bg-omsivo-royal p-2.5 shadow-2xl shadow-black/60">
            <div className="overflow-hidden rounded-[28px] bg-omsivo-deep">
                <div className="flex items-center justify-between bg-omsivo-navy px-5 py-4">
                    <Logo className="h-8 w-8" />
                    <Bell className="h-4 w-4 text-white/60" />
                </div>
                <div className="space-y-3 px-4 py-5">
                    <p className="font-mono text-[8px] uppercase tracking-[0.24em] text-flame">Project Dashboard</p>
                    <p className="font-display text-sm font-bold text-white">My Dream Home</p>
                    <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                        <motion.div className="h-full rounded-full bg-flame"
                            initial={{ width: "8%" }} whileInView={{ width: "64%" }}
                            viewport={{ once: true }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.4 }} />
                    </div>
                    <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/45">Stage 06 — Masonry in progress</p>
                    {[
                        { icon: FileText, label: "BOQ & Quotation", v: "Ready" },
                        { icon: HardHat, label: "Site Engineer", v: "Online" },
                        { icon: CreditCard, label: "Next Payment", v: "Stage 07" },
                    ].map((r) => (
                        <div key={r.label} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.04] px-3.5 py-3">
                            <span className="flex items-center gap-2.5 text-[11px] text-white/75">
                                <r.icon className="h-3.5 w-3.5 text-flame" />{r.label}
                            </span>
                            <span className="font-mono text-[9px] uppercase tracking-widest text-flame">{r.v}</span>
                        </div>
                    ))}
                    <div className="rounded-xl bg-flame/15 px-3.5 py-3 text-center">
                        <p className="font-display text-[11px] font-bold uppercase tracking-wider text-flame">Live Site View</p>
                    </div>
                </div>
            </div>
        </div>
        <div className="absolute -right-6 top-16 animate-float rounded-xl border border-white/15 bg-omsivo-deep/90 px-3.5 py-2.5 backdrop-blur-xl">
            <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50">Estimate</p>
            <p className="font-display text-xs font-bold text-white">Instant BOQ</p>
        </div>
    </div>
);

export const AppPromo = () => {
    const [settings, setSettings] = useState({ status: "coming_soon", android_link: "", ios_link: "" });
    const [modal, setModal] = useState(false);
    const live = settings.status === "live" && (settings.android_link || settings.ios_link);

    useEffect(() => {
        fetchAppSettings().then(setSettings).catch(() => {});
    }, []);

    const onDownload = () => {
        trackEvent(EVENTS.appCtaClick, { status: settings.status });
        if (!live) setModal(true);
    };

    return (
        <section id="app" data-testid="app-promo-section" className="blueprint-fine noise relative overflow-hidden py-24 lg:py-36">
            <div className="pointer-events-none absolute -left-48 bottom-0 h-[520px] w-[520px] rounded-full bg-omsivo-blue/35 blur-[150px]" />
            <div className="relative z-10 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <SectionHeading
                            id="app-heading"
                            dark
                            overline="Build Smarter with OMSIVO"
                            title={<>Your construction, <span className="text-flame">in your hands.</span></>}
                            description="The OMSIVO Customer App puts your entire project in one place — estimate, consult, book, buy, build, track, pay and maintain."
                        />
                        <div className="mt-10 grid grid-cols-2 gap-2.5 sm:grid-cols-3" data-testid="app-feature-grid">
                            {APP_FEATURES.map((f, i) => (
                                <Reveal key={f} delay={0.03 * i}>
                                    <div className="flex h-full items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3">
                                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-flame" />
                                        <span className="text-[12px] font-medium leading-tight text-white/80">{f}</span>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.2}>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                {live ? (
                                    <div className="flex gap-3">
                                        {settings.android_link && (
                                            <a href={settings.android_link} target="_blank" rel="noopener noreferrer" data-testid="app-download-android"
                                                className="rounded-full bg-flame px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white">Android App</a>
                                        )}
                                        {settings.ios_link && (
                                            <a href={settings.ios_link} target="_blank" rel="noopener noreferrer" data-testid="app-download-ios"
                                                className="rounded-full bg-flame px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white">iOS App</a>
                                        )}
                                    </div>
                                ) : (
                                    <button
                                        onClick={onDownload}
                                        data-testid="app-download-btn"
                                        className="flex items-center gap-3 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white/70 transition-colors hover:border-flame hover:text-white"
                                    >
                                        <Smartphone className="h-5 w-5 text-flame" />
                                        Download OMSIVO App
                                    </button>
                                )}
                                <span className="rounded-full bg-flame px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-white" data-testid="app-coming-soon-badge">
                                    Coming Soon
                                </span>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal className="lg:col-span-5" delay={0.15}>
                        <PhoneMockup />
                    </Reveal>
                </div>
            </div>

            <Dialog open={modal} onOpenChange={setModal}>
                <DialogContent className="max-w-md border-white/10 bg-omsivo-deep text-white" data-testid="app-coming-soon-modal">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-black uppercase tracking-wide">OMSIVO App — Coming Soon</DialogTitle>
                        <DialogDescription className="pt-2 text-base leading-relaxed text-white/70">
                            OMSIVO App is coming soon. We are building a smarter way to plan, build and manage your construction project.
                        </DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        </section>
    );
};
